import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable'
import { ModelListPanel } from './ModelListPanel'
import { DataViewer } from './DataViewer'
import { Database, AlertCircle, Loader2, LucideIcon } from 'lucide-react'
import type { Project } from '@/types/conversations'

interface DatabaseTabProps {
  project: Project
  onInsertPrompt?: (prompt: string) => void
}

interface InstanceStateMessageProps {
  icon: LucideIcon
  iconClassName: string
  bgClassName: string
  title: string
  description: string
}

function InstanceStateMessage({
  icon: Icon,
  iconClassName,
  bgClassName,
  title,
  description,
}: InstanceStateMessageProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-card">
      <div className={`h-16 w-16 rounded-full ${bgClassName} flex items-center justify-center mb-4`}>
        <Icon className={`h-8 w-8 ${iconClassName}`} />
      </div>
      <h3 className="font-semibold text-lg text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-md">{description}</p>
    </div>
  )
}

export function DatabaseTab({ project, onInsertPrompt }: DatabaseTabProps) {
  const { t } = useTranslation('conversations')
  const [selectedModel, setSelectedModel] = useState<{ appLabel: string; modelName: string } | null>(null)

  const handleModelSelect = (appLabel: string, modelName: string) => {
    setSelectedModel({ appLabel, modelName })
  }

  // Check if instance is running
  const isInstanceReady = project.runtime_state === 'running'
  const isInstanceStopped = project.runtime_state === 'stopped'
  const isInstanceStarting = project.runtime_state === 'starting'

  // Instance not ready - show appropriate message
  if (!isInstanceReady) {
    if (isInstanceStopped) {
      return (
        <InstanceStateMessage
          icon={Database}
          iconClassName="text-yellow-600 dark:text-yellow-400"
          bgClassName="bg-yellow-100 dark:bg-yellow-900/30"
          title={t('database.instanceStopped.title', 'Instance Stopped')}
          description={t('database.instanceStopped.description', 'Start the dev instance to view the database.')}
        />
      )
    }

    if (isInstanceStarting) {
      return (
        <InstanceStateMessage
          icon={Loader2}
          iconClassName="text-blue-600 dark:text-blue-400 animate-spin"
          bgClassName="bg-blue-100 dark:bg-blue-900/30"
          title={t('database.instanceStarting.title', 'Instance Starting')}
          description={t('database.instanceStarting.description', 'Please wait while the dev instance starts...')}
        />
      )
    }

    return (
      <InstanceStateMessage
        icon={AlertCircle}
        iconClassName="text-gray-600 dark:text-gray-400"
        bgClassName="bg-gray-100 dark:bg-gray-900/30"
        title={t('database.instanceNotReady.title', 'Instance Not Ready')}
        description={t('database.instanceNotReady.description', 'Start the dev instance to view database.')}
      />
    )
  }

  return (
    <div className="relative h-full">
      <div className="absolute inset-0">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Model List Panel */}
          <ResizablePanel defaultSize={25} minSize={15} maxSize={40}>
            <div className="h-full border-r">
              <ModelListPanel
                projectId={project.id}
                selectedModel={selectedModel}
                onModelSelect={handleModelSelect}
                onInsertPrompt={onInsertPrompt}
              />
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Data Viewer Panel */}
          <ResizablePanel defaultSize={75} minSize={50}>
            <div className="h-full">
              <DataViewer
                projectId={project.id}
                appLabel={selectedModel?.appLabel ?? null}
                modelName={selectedModel?.modelName ?? null}
              />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  )
}
